/// <reference path="./global.d.ts"/>

import { NgZone } from '@angular/core';
import { IAd } from '@ad/domain/ad';
import { isIpad, isSafari, preciseIOSVersion } from '@ad/utils/browser';
import { isUrl, isBase64 } from '@ad/utils/url';
import { createViewElement, isViewElementNode, forEachElement, isTextNode, isWidgetNode, forEachDataElement, resetNodeCid, isImageNode, isVideoNode } from './nodes/helpers';
import { IVersion, ICollection, IElement, IPosition, ISize, IFontStyle, IFontFamily, OneOfViewElements, OneOfTextDataNodes, FILTER_MAP, ActionOperationMethod, ActionTrigger, IState, OneOfElementDataNodes, MouseOrTouchEvent, NodeKind, OneOfTextElementPropertyKeys, OneOfTextViewElements, AppearenceStyles, ICreativeViewNode, SpanType, IText, ITextViewElement, IAction, IRectangleViewElement, IWidgetViewElement, IButtonViewElement, IEllipseElementDataNode, IWidgetElementDataNode, ITextElementDataNode, IButtonElementDataNode, textProperties, IEllipseViewElement, IElementDataNode, IImageElementDataNode, IActionOperation, ICreativeDataNode, IImageViewElement, IRectangleElementDataNode, IVideoElementDataNode, IVideoViewElement, INodeId } from '@studio/domain';
import { IWidgetRenderer } from './elements/widget/widget-renderer.header';
import { ResizeObserverCallback } from '@juggle/resize-observer/lib/ResizeObserverCallback';
import { ResizeObserverOptions } from '@juggle/resize-observer/lib/ResizeObserverOptions';
import { injectFontFace, isElementDescendantOfElement } from '../utils/dom-utils';
import { handleError } from '../utils/errors';
import { EventEmitter } from '../utils/event-emitter';
import { sanitizeUrl } from '../utils/sanitizer';
import { pick, getObjectDifference, deepEqual } from '../utils/utils';
import { DIContainer, Container, inject } from '../utils/di';
import { getOriginalUrl, ICroppingOptions, ImageOptimizerUrlBuilder } from '@ad/utils/image-optimizer';
import { IActionHandler, isCancelTrigger, registerActionEventHandlers } from './actions/actions.utils';
import { isElementVisibleAtTime, isTimeAt } from './animation.utils';
import { AnimatorLite } from './animator-lite';
import { IRichTextFactory, IVideoRendererFactory, T } from './creative.container';
import { ICSSProperties } from './css-styles';
import { IEnvironment } from './environment';
import { IFeedStore } from './elements/feed/feed-store.header';
import { Matrix } from '@studio/utils/matrix';
import { CreativeDataNode } from './nodes/data-nodes';
import { IAdditionalState, ICachedCssStyles, IPositionStyle, IRenderer, ISizeStyle } from './renderer.header';
import { getViewElementProperty, animationsToState, getShadowPropertyValue } from './rendering/states.utils';
import { IRichTextRenderOption } from './elements/rich-text/rich-text';
import { SVGBackground } from './svg-background';
import { isContentSpan } from './elements/rich-text/text-nodes';
import { INodeVisitor, visitAllNodes, visitAllChildren, visitOneNode } from './visitor';
import { WidgetEvent } from './elements/widget/events';

let instanceCounter = 0;

declare class ResizeObserver {
    constructor(callback: ResizeObserverCallback);
    observe(target: Element, options?: ResizeObserverOptions): void;
    unobserve(target: Element): void;
    disconnect(): void;
}

type RendererEvents =
    'textElementInteractionsStarted'
    | 'textElementInteractionsEnded'
    | 'click'
    | 'destroy';

export interface IRendererOption {
    ngZone?: NgZone;
    contentWindow?: Window;
    canvasSize?: ISize;
    elements?: ICollection<IElement>;
    versions?: ICollection<IVersion>;
    currentVersionId?: string;
    inDesignView?: boolean;
    fontFamilies?: ICollection<IFontFamily>;
    zoom?: number;
    brandId?: string;
}

export class Renderer extends EventEmitter<RendererEvents> implements IRenderer, INodeVisitor {
    private viewTree: ICreativeViewNode;

    get canvasSize_m(): ISize {
        return { width: this.viewTree.width, height: this.viewTree.height };
    }

    private get creativeSize(): ISize {
        return { width: this.creativeDocument.width, height: this.creativeDocument.height };
    }

    get isEditorMode(): boolean {
        return !window.bfstudio?.inPreviewMode && this.env_m.STUDIO_JS;
    }

    styleRules_m = new Map<string, {
        /**
         * Keep track of previous and current values both
         * the raw and actual CSSStyleRule as css values get trimmed when
         * applied in some cases (e.g transform matrices)
         */
        previous: ICachedCssStyles,
        current: ICachedCssStyles
    }>();
    rootElement: HTMLDivElement;
    time_m = 0;
    loop = 1;
    storedViewElements = new Map<string, OneOfViewElements>();
    destroyed_m = false;
    shouldRerender_m: boolean;
    private _isPlaying: boolean;

    private _window: Window;
    private static _scopeIndex = 0;
    private _scopeId: string;
    private _styleSheet: CSSStyleSheet;
    private _browserDocument: Document;
    private _htmlElements = new Map<string, HTMLElement>();
    private _fragment: DocumentFragment;
    private _styleElement?: HTMLStyleElement;
    private _mountPoint: HTMLElement;
    private _inDesignView: boolean;
    private _onlyRenderTextWithoutFontLoad: boolean;
    private _toggledStatesMap = new Map<string, IAdditionalState[]>();
    private _isTouchEvent_m?: boolean;
    private _preventNextClickThrough_m = false;
    private _imageDebounceTimeouts = this.env_m.STUDIO_JS ? new Map<string, number>() : undefined;
    preloadingElements = new Map<string, Promise<any>>();
    elements_m?: ICollection<IElement>;
    versions_m?: ICollection<IVersion>;
    get creativeDocument(): Readonly<ICreativeDataNode> {
        return this._creativeDocument;
    }
    private _creativeDocument: ICreativeDataNode;

    private logger?: { [key: string]: any } = window['logger'] && window.app === 'bannerflow'
        ? new window['logger']('Renderer' + instanceCounter++)
        : undefined;

    private _scale = 1;
    private _resizeObserver?: ResizeObserver;

    constructor(
        @DIContainer() private container: Container<T>,
        @inject(T.CREATIVE_DATA) creativeDocument: ICreativeDataNode,
        option: IRendererOption = {},
        @inject(T.ENVIRONMENT) public env_m: IEnvironment,
        @inject(T.FEED_STORE) public feedStore?: IFeedStore,
        @inject(T.WIDGET_RENDERER) public WidgetRenderer?: IWidgetRenderer,
        @inject(T.AD) public ad?: IAd) {
        super();
        this._creativeDocument = creativeDocument;
        this.elements_m = option.elements;
        this.versions_m = option.versions;
        this._inDesignView = option.inDesignView || false;
        this._window = option.contentWindow || window;
        this._browserDocument = this._window.document;
        this._styleElement = this._browserDocument.createElement('style');
        this._browserDocument.head!.appendChild(this._styleElement);
        this._styleSheet = this._styleElement.sheet as CSSStyleSheet;
        this._fragment = this._browserDocument.createDocumentFragment();

        this._resetViewTree();

        if (this.env_m.STUDIO_JS && this.feedStore) {
            this.feedStore.on('dataChanged', this.updateViewElementValues);
        }
    }

    getNodeIndex_m(node: INodeId): number {
        return this.creativeDocument.elements.findIndex(e => e.id === node.id);
    }

    setCreativeDocument(creativeDocument: ICreativeDataNode): void {
        this._creativeDocument = creativeDocument;
    }

    setPlaying_m(isPlaying: boolean): void {
        this._isPlaying = isPlaying;
    }

    getViewElementById<T extends OneOfViewElements>(id: string): Readonly<T | undefined> {
        return this.viewTree.elements.find(element => element.id === id) as Readonly<T | undefined>;
    }

    visitCreative_m(creative: ICreativeDataNode): void {
        const inDV = this.env_m.STUDIO_JS === true;
        this._scopeId = `b${Renderer._scopeIndex}`;
        this.rootElement = this._browserDocument.createElement('div');
        this.rootElement.setAttribute('data-banner', ''); // TODO: Remove this after image renderer is updated
        this.rootElement.setAttribute('data-creative', '');
        this._htmlElements.set(this._scopeId, this.rootElement);
        this._scaleViewTree(creative);
        this.viewTree.radius = creative.radius;
        this.viewTree.fill = creative.fill;
        this.viewTree.border = creative.border;
        this.viewTree.__rootElement = this.rootElement;

        this.insertStyle_m(this._scopeId, {
            width: '100%',
            height: '100%',
            boxSizing: 'border-box',
            position: 'relative',
            overflow: !inDV ? 'clip' : 'visible',
            fontFamily: 'Helvetica, Arial, Tahoma, "Microsoft Yahei", 微软雅黑, STXihei, 华文细黑, sans-serif',
            '-webkit-font-smoothing': 'antialiased',
            '-moz-osx-font-smoothing': 'grayscale'
        });

        this.setBackgroundElement_m(this.viewTree);
        this._fragment.appendChild(this.rootElement);
        Renderer._scopeIndex++;
        this.rootElement.addEventListener('mousemove', this._onCreativeMouseMove);
    }

    /**
     * Ugly workaround for mouseenter/mouseleave on text elements for actions.
     * Events propagate in nasty ways causing mousenter to get
     * stuck on random occasions so we have to "manually" trigger
     * mouseleave event if the element no longer is being hovered
     */
    private _onCreativeMouseMove = (e: MouseEvent): void => {
        if (this.isEditorMode) {
            return;
        }

        forEachElement(this.viewTree, element => {
            if (isViewElementNode(element)) {
                if (!element.__actionListeners) {
                    return;
                }

                for (const actionListener of element.__actionListeners) {
                    if (actionListener.trigger !== ActionTrigger.MouseLeave) {
                        continue;
                    }

                    const actionStateEnabled = this.getAdditionalStates_m(element.__data)
                        .find(additionalState =>
                            actionListener.action.operations
                                .find(op => op.value === additionalState.state.id));

                    if (actionStateEnabled && !actionStateEnabled?.animation?.isPlaying && !isElementDescendantOfElement(element.__rootElement!, e.target)) {
                        actionListener.event(e, true);
                    }
                }
            }
        });
    };

    visitRectangle_m(rectangle: IRectangleElementDataNode): IRectangleViewElement {
        const viewElement = this._createViewElementFromDataElement<IRectangleViewElement>(rectangle);

        this._createClickListener(viewElement);

        return viewElement;
    }

    visitEllipse_m(ellipse: IEllipseElementDataNode): IEllipseViewElement {
        const viewElement = this._createViewElementFromDataElement<IEllipseViewElement>(ellipse);

        this._createClickListener(viewElement);

        return viewElement;
    }

    visitText_m(text: ITextElementDataNode): ITextViewElement {
        return this.visitTextNode_m(text) as ITextViewElement;
    }

    visitButton_m(button: IButtonElementDataNode): IButtonViewElement {
        return this.visitTextNode_m(button) as IButtonViewElement;
    }

    visitTextNode_m(text: OneOfTextDataNodes): OneOfTextViewElements {
        const viewElement = this._createViewElementFromDataElement<OneOfTextViewElements>(text);
        viewElement.__rootElement!.classList.add('text');

        this.insertStyle_m(viewElement.elementCid!, this.getDefaultStyle_m(viewElement));

        if (process.env.STUDIO_JS) {
            if (text.font) {
                for (const [_, style] of text.characterStyles.entries()) {
                    if (style.font) {
                        this.injectFontFace(style.font);
                    }
                }
                this.injectFontFace(text.font);
            }
        }

        if (!(self as any).inServer) {
            this._renderText(viewElement);
        }

        return viewElement;
    }

    visitImage_m(image: IImageElementDataNode): IImageViewElement {
        const viewElement = this._createViewElementFromDataElement<IImageViewElement>(image);
        this._createClickListenerWithFeed(image, viewElement);

        return viewElement;
    }

    visitWidget_m(widgetDataElement: IWidgetElementDataNode): IWidgetViewElement {
        const viewElement = this._createViewElementFromDataElement<IWidgetViewElement>(widgetDataElement);

        const creativeElement = viewElement.__rootElement!.parentNode;

        if (creativeElement) {
            if (!creativeElement.querySelector(`#widget-${widgetDataElement.id}`)) {
                this.WidgetRenderer!.createWidget(viewElement, widgetDataElement);
            }
        }

        return viewElement;
    }

    visitVideo_m(videoDataElement: IVideoElementDataNode, skipVideoRenderer?: boolean): IVideoViewElement {
        const viewElement = this._createViewElementFromDataElement<IVideoViewElement>(videoDataElement);

        if (skipVideoRenderer) {
            return viewElement;
        }

        this._createClickListenerWithFeed(videoDataElement, viewElement);

        const createVideoRenderer = this.container.resolve<IVideoRendererFactory>(T.VIDEO_RENDERER_FACTORY);
        viewElement.__videoRenderer = createVideoRenderer(viewElement);

        return viewElement;
    }

    private _createClickListenerWithFeed(element: OneOfElementDataNodes, viewElement: OneOfViewElements): void {
        const onClick = (event: MouseEvent): void => {
            let deepLinkUrl = '';
            if (element.feed && this.feedStore) {
                deepLinkUrl = this.feedStore.getFeedValue(element.feed, element.id).targetUrl || '';
            }
            this._onClick(event, deepLinkUrl, viewElement);
        };

        this.on('destroy', () => {
            if (viewElement.__rootElement) {
                viewElement.__rootElement.removeEventListener('click', onClick);
            }
        });

        viewElement.__rootElement!.addEventListener('click', onClick);
    }

    private _createClickListener(viewElement: OneOfViewElements): void {
        const onElementClick = (event: MouseEvent): void => {
            if (this.isEditorMode) {
                return;
            }
            this._onClick(event, undefined, viewElement);
        };
        viewElement.__rootElement!.addEventListener('click', onElementClick);
        this.on('destroy', () => viewElement.__rootElement!.removeEventListener('click', onElementClick));
    }

    private _triggerClickActions(event: MouseOrTouchEvent, viewElement: OneOfViewElements): boolean {
        const actionListeners = viewElement.__actionListeners
            ?.filter(al =>
                al.trigger === ActionTrigger.Click
                && !al.action.disabled
            ) || [];

        if (actionListeners.length) {
            for (const listener of actionListeners) {
                if (listener.action.operations.some(op => op.method === ActionOperationMethod.OpenUrl)) {
                    listener.event(event);
                    break;
                }
                listener.event(event);
            }
            return true;
        }
        else {
            return false;
        }
    }

    private _onClick = (event: MouseOrTouchEvent, deepLinkUrl?: string, viewElement?: OneOfViewElements): void => {
        if (this.isEditorMode) {
            return;
        }
        if (this._preventNextClickThrough_m) {
            event.stopPropagation();
            this._preventNextClickThrough_m = false;
            return;
        }

        /**
         * Check if the element clicked on has any click actions and trigger those.
         * The triggered action will loop back here without a viewElement to
         * continue with the clickthrough
         */
        if (viewElement) {
            const isClickAction = this._triggerClickActions(event, viewElement);
            if (isClickAction) {
                return;
            }
        }

        if (!this.env_m.STUDIO_JS) {
            event.stopPropagation();
        }
        this.emit('click', [event, deepLinkUrl]);
    };

    setBackgroundElement_m(node: OneOfViewElements | ICreativeViewNode, overrides?: AppearenceStyles): void {
        if (!node.__svgBackground) {
            node.__svgBackground = new SVGBackground(this._window, node, this.env_m);
        }

        const svg = node.__svgBackground;

        const promise = svg.render_m(node, overrides);

        if (promise && (isImageNode(node) || isVideoNode(node))) {
            const id = node.id;

            if (!this.preloadingElements.has(id)) {
                this.logger?.verbose('[SvgBackground] adding image to preload:', id);
                this.preloadingElements.set(id, promise);
            }
        }
    }

    setCreativeStyle<Key extends keyof ICreativeDataNode, Property = ICreativeDataNode[Key]>(styleKey: Key, styleProperty: Property): void {
        Object.assign(this.creativeDocument, { [styleKey]: styleProperty });
        Object.assign(this.viewTree, { [styleKey]: styleProperty });

        this.setBackgroundElement_m(this.viewTree);
    }

    private _getImageUrl(element: IImageElementDataNode): string | undefined {
        const feed = element.feed;
        const viewElement = this.storedViewElements.get(element.id) as IImageViewElement;
        const feedValue = feed && this.feedStore!.getFeedValue(feed, element.id, element)?.value;
        const imageAsset = element.imageAsset;
        const feedUrl = typeof feedValue === 'string' ? sanitizeUrl(feedValue) : undefined;
        const inDV = this.env_m.DESIGN_VIEW && this.isEditorMode;
        const inMV = !this.env_m.DESIGN_VIEW && this.env_m.STUDIO_JS; // is there a better MV detection?
        const id = element.id;
        const url = feedUrl || imageAsset?.url;

        // DV contains base64 images while upload is ongoing, can't be optimized
        if (isBase64(url)) {
            return url;
        }

        // Note that the base64 check must be BEFORE this to not cause performance issues with regex
        if (!isUrl(url)) {
            return '';
        }

        // This is a feeded image but no feed value is available this "loop"
        if (feed && !feedUrl) {
            return '';
        }

        // Debounce optimization in DV if a URL already is set
        if ((inDV || inMV) && id && this._imageDebounceTimeouts) {
            const timeout = this._imageDebounceTimeouts.get(id);

            // Clear old timeout to achieve "debounce"
            if (timeout) {
                window.clearTimeout(timeout);
            }


            // Only debounce if URL haven't changed (hence size change)
            if (getOriginalUrl(viewElement.imageUrl) === url) {
                this._imageDebounceTimeouts.set(id, window.setTimeout(() => {
                    this._debouncedGetImageUrl(element);
                }, 1000));

                // Use current url meanwhile
                return viewElement.imageUrl;
            }
        }
        // Don't calculate new image sizes every tick if not needed since it's expensive
        else if (getOriginalUrl(viewElement.imageUrl) === url) {
            return viewElement.imageUrl;
        }

        const sizeLimit = {
            width: this.creativeDocument.width * this._scale * 2,
            height: this.creativeDocument.height * this._scale * 2,
        };


        let cropping: ICroppingOptions | undefined;
        // in DV we do load the image uncropped, as is
        if (!inDV) {
            cropping = ImageOptimizerUrlBuilder.getCroppingOptions(element);
        }

        return ImageOptimizerUrlBuilder.getUrlByElement_m(element, { feedUrl, force: inDV, sizeLimit, cropping });
    }

    /**
     * Update url when this function has not been called in X ms
     */
    private _debouncedGetImageUrl(element: IImageElementDataNode): void {
        if (!this.destroyed_m) {
            const viewElement = this.storedViewElements.get(element.id) as IImageViewElement;
            // Remove url to trigger prevent debounce in this._getImageUrl
            viewElement.imageUrl = undefined;
            viewElement.imageUrl = this._getImageUrl(element);

            // Update in DOM
            this.setBackgroundElement_m(viewElement);
        }
    }

    /**
     * Show a state fully without animating it. Used mainly for preview on canvas
     * @param element
     * @param state
     * @param rate value between 0 and 1
     */
    addAdditionalState_m(element: OneOfElementDataNodes, state: IState, rate: number): IAdditionalState {
        const timeStates = this.getAdditionalStates_m(element);
        let timeState = this._getAdditionalState(element, state);

        if (!timeState) {
            timeState = { state, rate };
            timeStates.push(timeState);
        }
        else {
            timeState.rate = rate;
        }

        this._toggledStatesMap.set(element.id, timeStates);

        return timeState;
    }

    private _getAdditionalState(element: OneOfElementDataNodes, state: IState): IAdditionalState | undefined {
        const timeStates = this.getAdditionalStates_m(element);
        return timeStates.find(s => state === s.state);
    }

    getAdditionalStates_m(element: OneOfElementDataNodes): IAdditionalState[] {
        return this._toggledStatesMap.get(element.id) || [];
    }

    clearAdditionalStates_m(): void {
        this._toggledStatesMap.forEach((_, elementId) => {
            const element = this._getDataElementById(elementId);
            if (element) {
                this.removeAdditionalStates_m(element);
            }
        });
    }

    removeAdditionalState_m(element: OneOfElementDataNodes, state: IState): void {
        const stateMap = this._toggledStatesMap.get(element.id);
        const activeStates = stateMap?.filter(s => s.state.id !== state.id) || [];
        this._toggledStatesMap.delete(element.id);
        this._toggledStatesMap.set(element.id, activeStates);
    }

    removeAdditionalStates_m(element: OneOfElementDataNodes, renderElement: boolean = true): void {
        const states = this.getAdditionalStates_m(element);
        this._toggledStatesMap.delete(element.id);
        if (states.length) {
            states.forEach(state => state.animation?.pause());
            if (renderElement) {
                this.renderElement_m(element);
            }
        }
    }

    private _renderText(textElement: OneOfTextViewElements): void {
        let richTextRenderer = textElement.__richTextRenderer;
        const textDataElement = textElement.__data;

        if (richTextRenderer) {
            richTextRenderer.rerender();
            return;
        }

        const options: IRichTextRenderOption = {
            feedStore: this.feedStore!,
            element: textDataElement,
            viewElement: textElement,
            skipFontLoad: this._onlyRenderTextWithoutFontLoad,
            document: this.creativeDocument,
            inDesignView: this._inDesignView,
        };
        const text: IText = {
            spans: textDataElement.__dirtyContent?.spans || textDataElement.content.spans, style: pick(Object.assign(textElement, textDataElement.__dirtyContent?.style || {}),
                'width',
                'height',
                ...textProperties
            )
        };
        const createRichText = this.container.resolve<IRichTextFactory>(T.RICH_TEXT_FACTORY);
        richTextRenderer = textElement.__richTextRenderer = createRichText(text, textElement.__rootElement!, options);
        richTextRenderer.init_m();

        const onElementInteractionsStarted = (): void => {
            this.emit('textElementInteractionsStarted', [richTextRenderer]);
        };
        const onElementInteractionsEnded = (): void => {
            this.emit('textElementInteractionsEnded', [richTextRenderer]);
        };
        const onElementDestroyed = (): void => {
            richTextRenderer.off('interactionsStarted', onElementInteractionsStarted);
            richTextRenderer.off('interactionsEnded', onElementInteractionsEnded);
            richTextRenderer.off('destroyed', onElementDestroyed);
        };

        const onSpanClicked = (event: MouseEvent, deepLinkUrl?: string): void => {
            this._onClick(event, deepLinkUrl);
        };

        const onTextClick = (event: MouseEvent): void => {
            let deepLinkUrl = '';
            try {
                if (this.feedStore) {
                    for (const span of textElement.__richTextRenderer.text_m.spans) {
                        if (isContentSpan(span) && span.style.variable) {
                            const feedValue = this.feedStore.getFeedValue(span.style.variable, span.style.variable.spanId!);
                            deepLinkUrl = feedValue.targetUrl!;
                            break;
                        }
                    }
                }
                // eslint-disable-next-line no-empty
            } catch (e) { }
            this._onClick(event, deepLinkUrl, textElement);
        };

        textElement.__rootElement!.addEventListener('click', onTextClick);

        this.on('destroy', () => {
            if (textElement.__rootElement) {
                textElement.__rootElement!.removeEventListener('click', onTextClick);
            }
        });

        // Try to remove listeners, renderText is triggered by undo/redo
        richTextRenderer.off('interactionsStarted', onElementInteractionsStarted);
        richTextRenderer.off('interactionsEnded', onElementInteractionsEnded);
        richTextRenderer.off('destroyed', onElementDestroyed);
        richTextRenderer.off('spanClicked', onSpanClicked);

        richTextRenderer.on('interactionsStarted', onElementInteractionsStarted);
        richTextRenderer.on('interactionsEnded', onElementInteractionsEnded);
        richTextRenderer.on('spanClicked', onSpanClicked);
        richTextRenderer.on('destroyed', onElementDestroyed);
    }

    /**
     * Set view element value without updating DOM
     * @param element
     * @param time
     */
    setViewElementValues_m(element: OneOfElementDataNodes, time: number, setStyle: boolean = true): void {
        this.time_m = time;
        const viewElement = this.storedViewElements.get(element.id)!;

        if (!viewElement) {
            throw new Error(`Could not find view node`);
        }

        viewElement.duration = element.duration;
        viewElement.time = element.time;

        // Always rerender on loop to make sure images/feeded elements doesn't blink
        const isLoop = time === 0;
        const additionalAnimationStates = this._toggledStatesMap.get(element.id)?.map(state => state);
        const isVisible = isElementVisibleAtTime(element, this.time_m) && !this._shouldHideOverlapElement(viewElement);

        // If not in view don't do any calculation and just hide the element.
        // We can't run this if setSyyle === false since that will mess up initialization
        if (setStyle && !isVisible && !isLoop) {
            viewElement.opacity = 0;
            this._renderStyleOnViewElement(viewElement);
        }
        else {
            const animationState = animationsToState(element, this.creativeSize, time, additionalAnimationStates);
            this._setViewElementValuesWithState(viewElement, element, animationState, setStyle);
        }
    }

    private _setViewElementValuesWithState(viewElement: OneOfViewElements, element: OneOfElementDataNodes, state: IState, setStyle: boolean = true): void {
        this.logger?.debug(`_setViewElementValuesWithState[${element.name}]`);

        const isWidget = isWidgetNode(element);

        viewElement.x = getViewElementProperty('x', element, state, this._scale);
        viewElement.y = getViewElementProperty('y', element, state, this._scale);
        viewElement.width = getViewElementProperty('width', element, state, isWidget ? undefined : this._scale);
        viewElement.height = getViewElementProperty('height', element, state, isWidget ? undefined : this._scale);
        viewElement.originX = getViewElementProperty('originX', element, state);
        viewElement.originY = getViewElementProperty('originY', element, state);
        viewElement.scaleX = getViewElementProperty('scaleX', element, state, isWidget ? this._scale : undefined);
        viewElement.scaleY = getViewElementProperty('scaleY', element, state, isWidget ? this._scale : undefined);
        viewElement.rotationX = getViewElementProperty('rotationX', element, state);
        viewElement.rotationY = getViewElementProperty('rotationY', element, state);
        viewElement.rotationZ = getViewElementProperty('rotationZ', element, state);
        viewElement.mirrorX = getViewElementProperty('mirrorX', element, state);
        viewElement.mirrorY = getViewElementProperty('mirrorY', element, state);
        viewElement.opacity = getViewElementProperty('opacity', element, state);
        viewElement.border = getViewElementProperty('border', element, state, this._scale);
        viewElement.fill = getViewElementProperty('fill', element, state);
        viewElement.shadows = getViewElementProperty('shadows', element, state, this._scale);
        viewElement.radius = getViewElementProperty('radius', element, state, this._scale);
        viewElement.filters = getViewElementProperty('filters', element, state);
        viewElement.hidden = getViewElementProperty('hidden', element, state);
        viewElement.perspective = this._getPerspective(element);
        viewElement.locked = element.locked;

        // Need to adjust x & y to compensate for not scaling width and height
        if (isWidget && this._scale !== 1) {
            viewElement.x -= (viewElement.width * (1 - this._scale)) / 2;
            viewElement.y -= (viewElement.height * (1 - this._scale)) / 2;
        }

        if (isImageNode(viewElement)) {
            const imgElement = element as IImageElementDataNode;
            if (imgElement.feed) {
                viewElement.feed = getViewElementProperty('feed', imgElement, state);
                imgElement.imageAsset = undefined;
            }
            else if (imgElement.imageAsset) {
                viewElement.feed = imgElement.feed = undefined;
            }

            // Use this only
            viewElement.imageUrl = this._getImageUrl(viewElement.__data);
        }

        // maps to text prop changes, true if property changed, undefined if not changed
        const textPropertyChangedMap: { [key in OneOfTextElementPropertyKeys]?: boolean } = {};
        if ((isTextNode(viewElement))) {
            const previousProps = {};
            const dynamicProperties: OneOfTextElementPropertyKeys[] = [
                'textColor'
            ];
            for (const prop of dynamicProperties) {
                previousProps[prop] = viewElement[prop];
            }

            // scale all text spans shadows as well - STUDIO-8284
            viewElement.content.spans.forEach(span => {
                if (span.type === SpanType.Word
                    || span.type === SpanType.Composition
                    || span.type === SpanType.Space
                    || span.type === SpanType.Variable
                    || span.type === SpanType.Newline) {
                    if (span.style.textShadows) {
                        span.style.textShadows = span.style.textShadows.map(shadow => getShadowPropertyValue('TextShadow[]', shadow, this._scale));
                    }
                }
            });

            viewElement.textColor = getViewElementProperty('textColor', element, state);
            viewElement.textShadows = getViewElementProperty('textShadows', element, state, this._scale);
            viewElement.underline = getViewElementProperty('underline', element, state);
            viewElement.strikethrough = getViewElementProperty('strikethrough', element, state);
            viewElement.uppercase = getViewElementProperty('uppercase', element, state);
            viewElement.textOverflow = getViewElementProperty('textOverflow', element, state);
            viewElement.lineHeight = getViewElementProperty('lineHeight', element, state);
            viewElement.fontSize = getViewElementProperty('fontSize', element, state, this._scale);
            viewElement.maxRows = getViewElementProperty('maxRows', element, state);
            viewElement.padding = getViewElementProperty('padding', element, state, this._scale);
            viewElement.characterSpacing = getViewElementProperty('characterSpacing', element, state);
            viewElement.horizontalAlignment = getViewElementProperty('horizontalAlignment', element, state);
            viewElement.verticalAlignment = getViewElementProperty('verticalAlignment', element, state);

            const dirty = (element as OneOfTextDataNodes).__dirtyContent;
            const fontSize = dirty?.style.fontSize ?? viewElement.fontSize;
            const textShadows = dirty?.style.textShadows ?? viewElement.textShadows;
            const padding = dirty?.style.padding ?? viewElement.padding;
            viewElement.__richTextRenderer?.setStyle_m('textShadows', textShadows, viewElement.__richTextRenderer.style);
            viewElement.__richTextRenderer?.setStyle_m('fontSize', fontSize, viewElement.__richTextRenderer.style);
            viewElement.__richTextRenderer?.setStyle_m('padding', padding, viewElement.__richTextRenderer.style);

            for (const prop of dynamicProperties) {
                textPropertyChangedMap[prop] = !deepEqual(viewElement[prop], previousProps[prop]);
            }
        }

        if (isVideoNode(viewElement) && viewElement.__videoRenderer) {
            viewElement.__videoRenderer.updateVideo();
        }

        if (isWidget) {
            element.__widget?.emit(WidgetEvent.ViewNodeChanged, [viewElement]);
        }

        if (setStyle) {
            this.setBackgroundElement_m(viewElement);
            this._renderStyleOnViewElement(viewElement, textPropertyChangedMap);
        }
    }

    private _shouldHideOverlapElement(viewElement: OneOfViewElements): boolean {
        const rounding = 0.001;
        const viewEndTime = viewElement.time + viewElement.duration;

        // Only do this if current time is at end of element
        if (isTimeAt(viewEndTime, this.time_m, rounding)) {
            return this.creativeDocument.elements.some(({ time }) => isTimeAt(viewEndTime, time, rounding));
        }
        return false;
    }
    /**
     * Update style on DOM with current viewElement values
     */
    private _renderStyleOnViewElement(viewElement: OneOfViewElements, textPropertyChangedMap?: { [key in OneOfTextElementPropertyKeys]?: boolean }): void {
        const styleRule = this.styleRules_m.get(viewElement.elementCid!);

        if (!styleRule) {
            throw new Error(`Could not get stylerule for element. ${this.env_m.STUDIO_JS ? 'This is most likely caused by the element no longer existing from an undo/redo.' : ''}`);
        }

        const {
            current: currentStyle,
            previous: previousStyle
        } = styleRule;

        currentStyle.rawStyleValue = this.getDefaultStyle_m(viewElement);

        const changedStyles = getObjectDifference(previousStyle.rawStyleValue, currentStyle.rawStyleValue);

        const styleProperties = [
            'pointerEvents',
            'transform',
            'width',
            'height',
            'opacity',
            'filter',
            'visibility'
        ];

        for (const property of styleProperties) {
            if (property in changedStyles) {
                viewElement.__rootElement!.style[property] = changedStyles[property];
            }
        }

        // We only want to change backface visibility if it's not undefined
        if (changedStyles.backfaceVisibility) {
            previousStyle.styleRule.style.backfaceVisibility = changedStyles.backfaceVisibility;
        }

        if (isTextNode(viewElement)) {
            const sizeChanged = typeof changedStyles.width !== 'undefined'
                || typeof changedStyles.height !== 'undefined';

            let elementIsFeeded = false;
            const feedStore = viewElement.__richTextRenderer.feedStore_m;

            if (sizeChanged || (this.shouldRerender_m && feedStore)) {
                elementIsFeeded = !!feedStore?.elements.get(viewElement.id) ||
                    !!viewElement.__richTextRenderer.text_m.spans
                        .find(span => span.type === SpanType.Variable) ||
                    !!viewElement.__dirtyContent?.spans
                        .find(span => span.type === SpanType.Variable);
            }

            if (textPropertyChangedMap?.textColor) {
                const textColor = viewElement.textColor;
                viewElement.__richTextRenderer.applyOnlyAnimatedStyleOnElement_m({ textColor });
            }

            if (elementIsFeeded || sizeChanged) {
                viewElement.__richTextRenderer.rerender(false, viewElement);
            }
        }

        previousStyle.rawStyleValue = currentStyle.rawStyleValue;
        previousStyle.styleRule = currentStyle.styleRule;
    }

    setAllViewElementsValues_m(time: number, shouldRerender?: boolean): void {
        if (this.destroyed_m) {
            return;
        }

        if (shouldRerender) {
            this.shouldRerender_m = shouldRerender;
        }

        forEachDataElement(this.creativeDocument, element => {
            if (!element.hidden) {
                this.setViewElementValues_m(element, time);
            }
        });
        this.shouldRerender_m = false;
    }

    updateCreativeValues(): void {
        // rescale creative background
        this.setBackgroundElement_m(this.viewTree);
    }

    updateViewElementValues = (): void => {
        this.setAllViewElementsValues_m(this.time_m);
    };

    renderElement_m(element: OneOfElementDataNodes): void {
        this.setViewElementValues_m(element, this.time_m);
    }

    renderElementById_m(id: string): void {
        const element = this._getDataElementById(id);
        if (element) {
            this.renderElement_m(element);
        }
    }

    setActions_m(dataElement: OneOfElementDataNodes): void {
        const viewElement = this.viewTree.elements.find(el => el.id === dataElement.id)!;

        let ActionHandler: IActionHandler = {
            callback: (event: MouseOrTouchEvent, trigger: ActionTrigger, forceClearStates?: boolean) => {

                if (this.isEditorMode) {
                    return;
                }

                /**
                 * Treat all following events explicitly for touch
                 * to avoid actions potentially triggering more than once
                 */
                if (trigger === ActionTrigger.TouchStart) {
                    this._isTouchEvent_m = true;
                }

                /**
                 * Special check to properly trigger mouseleave
                 * events for text elements
                 */
                if (trigger === ActionTrigger.MouseLeave && !forceClearStates && event.target !== viewElement.__rootElement) {
                    return;
                }

                if (this._isTouchEvent_m && trigger.startsWith('mouse')) {
                    return;
                }

                const target = event instanceof MouseEvent
                    ? event.relatedTarget
                    : event.target;

                if (trigger !== ActionTrigger.MouseLeave && target && !this._browserDocument.body.contains(target as HTMLElement)) {
                    return;
                }

                dataElement.actions
                    .filter(action => action.triggers.find(tr => tr === trigger))
                    .forEach(action => {

                        if (action.disabled) {
                            return;
                        }

                        action.operations.forEach(operation => {
                            switch (operation.method) {
                                case ActionOperationMethod.OpenUrl:
                                    this._onClick(event, operation.value as string);
                                    break;

                                case ActionOperationMethod.SetState:
                                case ActionOperationMethod.RemoveState:
                                case ActionOperationMethod.ClearStates:
                                    if (action.preventClickThrough) {
                                        this._preventNextClickThrough_m = true;
                                    }
                                    this._setActionState(operation, action, trigger);
                                    break;
                            }
                        });

                    });
            }
        };

        ActionHandler = {
            ...ActionHandler,
            ...registerActionEventHandlers(ActionHandler)
        };

        viewElement.__actionListeners = viewElement.__actionListeners || [];

        for (const listener of viewElement.__actionListeners) {
            // useCapture should be true, since the addEventListener set it to true
            viewElement.__rootElement!.removeEventListener(listener.trigger, listener.event, true);
        }

        viewElement.__actionListeners = [];

        for (const action of dataElement.actions) {
            if (action.disabled) {
                continue;
            }

            for (const trigger of action.triggers) {
                /**
                 * Clicks has to be handled from onClick
                 * to avoid event emission race-conditions
                */
                if (trigger !== ActionTrigger.Click) {
                    if (trigger === ActionTrigger.TouchStart) {
                        viewElement.__rootElement!.addEventListener(trigger, ActionHandler[trigger], { passive: true });
                    }
                    else {
                        viewElement.__rootElement!.addEventListener(trigger, ActionHandler[trigger], true);
                    }
                }

                viewElement.__actionListeners.push({
                    trigger,
                    event: ActionHandler[trigger],
                    action
                });
            }
        }
    }

    private _setActionState(operation: IActionOperation, action: IAction, trigger: ActionTrigger): void {
        const stateTargetViewElement = this.viewTree.elements.find(el => el.id === operation.target);

        if (!stateTargetViewElement) {
            return;
        }

        const stateTargetDataElement = stateTargetViewElement.__data;
        const stateId = operation.value;

        if (!stateId && (operation.method === ActionOperationMethod.SetState || operation.method === ActionOperationMethod.RemoveState)) {
            return;
        }

        /**
         * Only trigger the backup MouseLeave if the MouseDown
         * has happened for pressed states
         **/

        if (action.templateId === 'reserved-pressed' && isCancelTrigger(trigger)) {
            const pressedAction = stateTargetDataElement.actions
                .find(targetAction => targetAction.templateId === 'reserved-pressed' && targetAction.operations.find(ops => ops.value))!;

            const currentTimeState = this.getAdditionalStates_m(stateTargetDataElement).find(timeState => timeState.state.id === pressedAction.operations[0].value);

            if (!currentTimeState || currentTimeState.rate === 0) {
                return;
            }
        }

        const targetState = stateTargetDataElement.states
            .find(state => state.id === stateId);

        const duration = operation.animation!.duration ?? 0.2;
        const timingFunction = operation.animation!.timingFunction;

        /**
         * When the default animator is paused we'll have to rely
         * on animatorlite's own tick to trigger the
         * rerenderering of the element
         */
        const animatorLiteCallback = (): void => {
            if (this._isPlaying) {
                return;
            }
            this.setViewElementValues_m(stateTargetDataElement, this.time_m);
        };
        const settings = { duration, timingFunction, callback: animatorLiteCallback };

        // Animate in a state
        if (targetState) {
            const currentTimeState = this.getAdditionalStates_m(stateTargetDataElement)
                .find(additionalState =>
                    additionalState.animation && additionalState.state.id === targetState.id
                );

            // State is already visible (with rate 0-1)
            if (currentTimeState) {
                if (operation.method === ActionOperationMethod.RemoveState) {
                    this.getAdditionalStates_m(stateTargetDataElement)
                        .filter(additionalState => targetState === additionalState.state && additionalState.animation)
                        .forEach(timeState => {
                            if (timeState?.animation) {
                                timeState.animation.to({ rate: 0 }, settings);
                            }
                        });
                }
                else {
                    currentTimeState.animation!.to({ rate: 1 }, settings);
                }
            }
            // Add and animate in the new state from rate 0
            else {
                if (operation.method === ActionOperationMethod.SetState) {
                    const timeState = this.addAdditionalState_m(stateTargetDataElement, targetState, 0);
                    timeState.animation = AnimatorLite.to(timeState, { rate: 1 }, settings);
                    timeState.animation.onCompleted(() => {
                        if (timeState.rate === 0) {
                            this.removeAdditionalState_m(stateTargetDataElement, targetState);
                        }
                    });
                }
            }
        }
        // Remove state
        else if (!targetState || operation.method === ActionOperationMethod.ClearStates) {
            this.getAdditionalStates_m(stateTargetDataElement)
                .filter(state => state.animation)
                .forEach(timeState => {
                    if (timeState?.animation) {
                        timeState.animation.to({ rate: 0 }, settings);
                    }
                });
        }
    }

    rerender_m(creative: CreativeDataNode): void {
        this.logger?.debug(`rerender_m`);

        this._onlyRenderTextWithoutFontLoad = false;
        this.setCreativeDocument(creative);
        this._clearElementHandlers();

        this._resetViewTree();

        this._fragment = this._browserDocument.createDocumentFragment();

        resetNodeCid();
        this._mountPoint.innerHTML = '';

        this._removeStyles();
        visitAllNodes(this.creativeDocument, this);
        this.rootElement.addEventListener('click', this._onClick);

        this._setBaseStyles(this._onlyRenderTextWithoutFontLoad);

        this.updateElementOrder_m();

        this._mountPoint.appendChild(this._fragment);
        this.WidgetRenderer?.creativeApi.bind();
    }

    private _resetInitialState(time: number, onlyRenderTextWithoutFontLoad: boolean, resetNodeId: boolean = true): void {
        // clear host element
        this._mountPoint.innerHTML = '';

        this.time_m = time;
        this._onlyRenderTextWithoutFontLoad = onlyRenderTextWithoutFontLoad;

        if (resetNodeId) {
            resetNodeCid();
        }

        this._resetViewTree();
        this._removeStyles();
    }

    private _setBaseStyles(onlyRenderTextWithoutFontLoad: boolean): void {
        this._insertStyles();
        for (const element of this.creativeDocument.elements) {
            if (element.hidden) {
                continue;
            }
            if (onlyRenderTextWithoutFontLoad) {
                if (!isTextNode(element)) {
                    continue;
                }
            }
            const viewElement = this.storedViewElements.get(element.id)!;
            const styleRule = this.styleRules_m.get(viewElement.elementCid!)!.current.styleRule;
            const willChange: string[] = [];
            for (let i = 0; i < styleRule.style.length; i++) {
                if (styleRule.style[i] === 'transform') {
                    if ((this._scale !== 1 && isTextNode(element)) || this._scale === 1) {
                        willChange.push(styleRule.style[i]);
                    }
                }

                if (!!styleRule.style[i].match(/(opacity|perspectiveOrigin)/)) {
                    willChange.push(styleRule.style[i]);
                }
            }
            styleRule.style.willChange = willChange.join(', ');
        }

        this.updateElementOrder_m();
    }

    initialize(mountPoint: HTMLElement, time: number = 0, onlyRenderTextWithoutFontLoad: boolean = false): void {
        this.logger?.verbose('initialize');

        if (this.destroyed_m) {
            return;
        }

        // 1. set host element
        this._mountPoint = mountPoint;

        // 2. reset to initial state
        this._resetInitialState(time, onlyRenderTextWithoutFontLoad, false);

        // 3. determine & set scale
        const { width } = mountPoint.getBoundingClientRect();
        const originalWidth = this.creativeDocument.width;
        const scale = this._calculateScale(width, originalWidth);
        this._setScale(scale);

        // 4. visit creative and all elements
        visitAllNodes(this.creativeDocument, this, onlyRenderTextWithoutFontLoad);

        // 5. insert base styles
        this._setBaseStyles(onlyRenderTextWithoutFontLoad);

        // 6. append creative container to host
        this._mountPoint.appendChild(this._fragment);

        // 7. add observers
        this.rootElement.addEventListener('click', this._onClick);
        // should be done once the render is fully done, unfortunately the animated creative does magic with feeds and not fully inits all elements
        this._initResizeObserver(mountPoint);
    }

    /**
     * @deprecated - use the correct initialize flow - initialize()
     */
    render(mountPoint: HTMLElement, time: number = 0, onlyRenderTextWithoutFontLoad: boolean = false): void {
        this.logger?.verbose('render');

        this.time_m = time;
        this._onlyRenderTextWithoutFontLoad = onlyRenderTextWithoutFontLoad;

        const { width } = mountPoint.getBoundingClientRect();
        const originalWidth = this.creativeDocument.width;

        const scale = this._calculateScale(width, originalWidth);
        this._setScale(scale);

        mountPoint.innerHTML = '';

        this._resetViewTree();
        this._removeStyles();

        if (this.destroyed_m) {
            return;
        }
        // Work around to get the cycle of WidgetRenderer's initialization correct
        this.visitCreative_m(this.creativeDocument);

        visitAllChildren(this.creativeDocument.elements, this, onlyRenderTextWithoutFontLoad);
        this.rootElement.addEventListener('click', this._onClick);

        this._setBaseStyles(onlyRenderTextWithoutFontLoad);

        this.updateElementOrder_m();
        mountPoint.appendChild(this._fragment);
        this._mountPoint = mountPoint;

        // should be done once the render is fully done, unfortunately the animated creative does magic with feeds and not fully inits all elements
        this._initResizeObserver(mountPoint);
    }

    private _clearElementHandlers(): void {
        for (const element of this.viewTree.elements) {
            if (isTextNode(element)) {
                element.__richTextRenderer.destroy_m();
            }
            if (isVideoNode(element)) {
                element.__videoRenderer.destroy();
            }
            const styleRule = this.styleRules_m.get(element.elementCid!)!;
            styleRule.current.styleRule.style.willChange = '';
            styleRule.previous.styleRule.style.willChange = '';
            if (element.__rootElement) {
                element.__rootElement.removeEventListener('click', this._onClick);
            }
        }
        this.rootElement?.removeEventListener('click', this._onClick);
        this.rootElement?.removeEventListener('mousemove', this._onCreativeMouseMove);
    }

    destroyElement_m(element: OneOfElementDataNodes, isDefinitivelyDestroyed: boolean = true): void {
        const elementIndex = this.creativeDocument.elements.findIndex((e) => e.id === element.id);

        if (isDefinitivelyDestroyed) {
            this.creativeDocument.elements.splice(elementIndex, 1);
            if (element.hidden) {
                return;
            }
        }

        if (elementIndex === -1) {
            throw new Error(`Could not find ${element.kind}`);
        }

        this.removeAdditionalStates_m(element, false);
        const viewElement = this.storedViewElements.get(element.id)!;
        this.viewTree.elements.splice(this.viewTree.elements.indexOf(viewElement), 1);
        const cid = viewElement.elementCid!;
        const styleRule = this.styleRules_m.get(cid)!;
        this._removeStyle(styleRule.current.styleRule);
        const imageTimeout = this._imageDebounceTimeouts?.get(cid);

        if (imageTimeout) {
            window.clearTimeout(imageTimeout);
        }

        if (isTextNode(viewElement) && viewElement.__richTextRenderer) {
            viewElement.__richTextRenderer.destroy_m();
            viewElement.__richTextRenderer = undefined as any;
        }

        if (isWidgetNode(viewElement)) {
            viewElement.__data.__widget?.destroy();
        }

        if (isVideoNode(viewElement)) {
            viewElement.__videoRenderer.destroy();
        }

        const nodeElement = viewElement.__safari3dElement || viewElement.__rootElement;

        if (!nodeElement) {
            throw new Error(`HTML element does not exist on canvas.`);
        }

        this.rootElement.removeChild(nodeElement);
    }

    destroy(): void {
        this.emit('destroy');
        if (this._styleElement && this._styleElement.parentNode) {
            (this._styleElement.parentNode as HTMLElement).removeChild(this._styleElement);
        }

        this._clearElementHandlers();

        this.clearAdditionalStates_m();

        if (this.feedStore) {
            this.feedStore.destroy();
            this.feedStore.off('dataChanged', this.updateViewElementValues);
        }

        while (this.viewTree.elements.length) {
            this.destroyElement_m(this.viewTree.elements[0].__data);
        }

        this._styleSheet = undefined as any;
        this.styleRules_m.clear();

        if (this.WidgetRenderer) {
            this.WidgetRenderer.destroy();
        }
        this.storedViewElements.clear();

        this._resizeObserver?.disconnect();
        this.destroyed_m = true;
        this.clearEvents();
    }

    private _createViewElementFromDataElement<T extends OneOfViewElements>(dataElement: OneOfElementDataNodes): T {
        const viewElement = createViewElement(dataElement);
        this.storedViewElements.set(dataElement.id, viewElement);
        this.setViewElementValues_m(dataElement, this.time_m, false);

        viewElement.__rootElement = this._createElement(viewElement);
        this.insertStyle_m(viewElement.elementCid!, {
            ...this.getDefaultStyle_m(viewElement)
        });
        this.setBackgroundElement_m(viewElement);

        this.viewTree.elements.push(viewElement);

        this.setActions_m(dataElement);

        return viewElement as T;
    }

    /**
     * Update order of view elements and DOM based on the order of the dataElements
     */
    updateElementOrder_m(): void {
        const elements = this.creativeDocument.elements;
        const viewElements = this.viewTree.elements;

        // Sort view elements in the same order as the data elements.
        viewElements.sort((a, b) => elements.indexOf(a.__data) - elements.indexOf(b.__data));

        // Update indexes on DOM by changing order of elements
        viewElements.forEach((element, index) => this._moveViewElementInDom(element, index));
    }

    /**
     * Used to bring a text to top while editing text.
     */
    bringElementToTop_m(element: OneOfViewElements): void {
        this._moveViewElementInDom(element, Number.MAX_VALUE);
    }

    /**
     * Move viewElement in DOM to a certain index.
     */
    private _moveViewElementInDom(element: OneOfViewElements, newIndex: number): void {
        const domElement = element.__safari3dElement || element.__rootElement;
        const parent = domElement?.parentNode;

        if (domElement && parent) {

            // Exclude SVG background from list of child elements
            const children = Array.from(parent.children).filter(e => e.tagName === 'DIV');
            const currentIndex = children?.length ? children.indexOf(domElement) : -1;

            if (currentIndex > -1 && currentIndex !== newIndex) {
                // null puts the element in the end
                parent.insertBefore(domElement, children[newIndex] || null);
            }
        }
    }

    getDefaultStyle_m(element: OneOfViewElements): any {
        const elementIsVisibleAtTime = isElementVisibleAtTime(element.__data, this.time_m);

        /**
         * Show and hide elements with opacity and pointer-events instead of
         * relying in visiblity due to a bug in widgets on iOS 14
         */
        const opacity = (typeof element.opacity === 'number'
            ? ({ opacity: `${elementIsVisibleAtTime ? element.opacity : 0}` })
            : undefined);

        const filters = element.filters || {};
        const filterString = (Object.entries(filters) as Array<[keyof typeof filters, (typeof filters)[keyof typeof filters]]>)
            .filter(([_, filter]) => filter?.value !== undefined)
            // We can assert that filter exists as we filtered it (heh)
            .map(([key, filter]) => {
                const filterUnit = FILTER_MAP[key]?.unit;
                return `${key}(${filter!.value!.toFixed(0)}${filterUnit})`;
            })
            .join(' ');

        // See comment about opacity above
        let isIOS = false;
        if (!isIpad && preciseIOSVersion.simple <= 0) {
            isIOS = true;
        }

        const defaultStyles = {
            pointerEvents: elementIsVisibleAtTime ? 'auto' : 'none',
            ...this.getPositionAndSizeStyle_m(element),
            ...(typeof element.radius === 'number' ? { borderRadius: `${element.radius}px` } : undefined),
            ...opacity,
            visibility: isIOS || elementIsVisibleAtTime ? 'visible' : 'hidden',
            filter: filterString || 'unset'
        } as any;

        return defaultStyles;
    }

    rerenderText_m(): void {
        for (const element of this.viewTree.elements) {
            if (isTextNode(element)) {
                element.__richTextRenderer.rerender();
            }
        }
    }

    private _getPerspective(element: IElementDataNode): number {
        return Math.max(element.width, element.height) * 2;
    }

    getPositionAndSizeStyle_m(element: OneOfViewElements): IPositionStyle & ISizeStyle {
        return {
            width: element.width + 'px',
            height: element.height + 'px',
            transform: this.getTransformMatrix_m(element),
        };
    }

    getTransformString_m(element: OneOfViewElements, excludeTranslate: boolean = false): string {

        const scale = this._getScaleOfElement(element);
        let transformString = excludeTranslate ? '' : `translate(${element.x}px, ${element.y}px)`;

        if (element.rotationZ) {
            transformString += ` rotateZ(${element.rotationZ}rad)`;
        }
        if (element.rotationY) {
            transformString += ` rotateY(${element.rotationY}rad)`;
        }
        if (element.rotationX) {
            transformString += ` rotateX(${element.rotationX}rad)`;
        }

        transformString += ` scaleX(${scale.x})`;
        transformString += ` scaleY(${scale.y})`;

        return transformString;
    }

    /**
     * Get a css transform matrix3d from view element.
     * @param element
     * @returns
     */
    getTransformMatrix_m(element: OneOfViewElements): string {

        const scale = this._getScaleOfElement(element);
        const matrix = new Matrix();

        matrix.translate_m(element.x, element.y);

        // Set perspective AFTER moving but BEFORE rotating element
        // to always keep perspective-origin in the center of the element
        matrix.perspective_m(element.perspective || 800);

        // Order of these is crucial to 3D-rotate in an expected way
        if (element.rotationZ) {
            matrix.rotateZ_m(element.rotationZ);
        }
        if (element.rotationY) {
            matrix.rotateY_m(element.rotationY);
        }
        if (element.rotationX) {
            matrix.rotateX_m(element.rotationX);
        }

        matrix.scale_m(scale.x, scale.y);

        // Hack for blurry images when not "3D" in responsive ads and zoomed canvas.
        // Safari messes up z-index for non 3d rotated elements
        if (!element.rotationY && !element.rotationX && element.kind === NodeKind.Image) {
            matrix.rotateY_m(Math.PI * 2);
        }

        return matrix.toCSS_m();
    }

    private _getScaleOfElement(styles: OneOfViewElements): IPosition {
        return {
            x: (typeof styles.scaleX === 'number' ? styles.scaleX : 1) * (styles.mirrorX ? -1 : 1),
            y: (typeof styles.scaleY === 'number' ? styles.scaleY : 1) * (styles.mirrorY ? -1 : 1)
        };
    }

    private _removeStyle(rule: CSSStyleRule): void {
        for (let i = this._styleSheet.cssRules.length - 1; i >= 0; i--) {
            if ((this._styleSheet.cssRules[i] as any).selectorText === rule.selectorText) {
                this._styleSheet.deleteRule(i);
                break;
            }
        }
    }

    private _removeStyles(): void {
        this.styleRules_m.clear();
        const count = this._styleSheet && this._styleSheet.cssRules ? this._styleSheet.cssRules.length : 0;
        for (let i = count - 1; i > 2; i--) {
            this._styleSheet.deleteRule(i);
        }
    }

    private _insertStyles(): void {
        const styleSheet = this._styleSheet;

        // Safari hack to not overlap element
        if (isSafari) {
            styleSheet.insertRule(`#${this._scopeId} .safari-3d {position:absolute;left:0;top:0;bottom:0;right:0,pointer-events:none; transform: translateZ(0); }`,
                styleSheet.cssRules.length);
        }

        // Element divs
        styleSheet.insertRule(`#${this._scopeId} .element{position:absolute;left:0;top:0;}`,
            styleSheet.cssRules.length);

        // Text div inside element divs
        styleSheet.insertRule(`#${this._scopeId} .text>div{position:relative;}`,
            styleSheet.cssRules.length);
    }

    insertStyle_m(cid: string, style: ICSSProperties): void {
        let selector: string;
        if (cid !== this._scopeId) {
            selector = `#${this._scopeId} #${cid}`;
        }
        else {
            selector = '#' + cid;
        }
        let rules = '';
        for (const rule in style) {
            if (style[rule]) {
                rules += rule.replace(/([A-Z])/g, (m) => '-' + m.toLowerCase()) + ':' + style[rule] + ';';
            }
        }

        // Insert rules for element div
        this._styleSheet.insertRule(`${selector}{${rules}}`, this._styleSheet.cssRules.length);
        const styleRule = this._styleSheet.cssRules[this._styleSheet.cssRules.length - 1] as CSSStyleRule;
        const styles = this._getStyleValues(styleRule);
        this.styleRules_m.set(cid, {
            previous: {
                styleRule: styleRule,
                rawStyleValue: styles
            },
            current: {
                styleRule: styleRule,
                rawStyleValue: styles
            }
        });
        const element = this._htmlElements.get(cid)!;
        element.setAttribute('id', cid);
    }

    private _getStyleValues(styleRule: CSSStyleRule): CSSStyleDeclaration {
        const styles = {} as CSSStyleDeclaration;
        for (let i = 0; i < styleRule.style.length; i++) {
            const style = styleRule.style[i];
            styles[style] = styleRule.style[styleRule.style[i]];
        }
        return styles;
    }

    private _createElement(element: OneOfViewElements): HTMLDivElement {
        const htmlElement = this._browserDocument.createElement('div');
        this._htmlElements.set(element.elementCid!, htmlElement);

        if (this.env_m.STUDIO_JS && process.env.IN_TEST && element.name) {
            htmlElement.setAttribute('data-test-name', element.name);
        }

        htmlElement.classList.add('element', 'kind-' + element.kind);

        if (isSafari) {
            const wrapper3d = this._browserDocument.createElement('div');
            wrapper3d.classList.add('safari-3d');
            wrapper3d.appendChild(htmlElement);
            element.__safari3dElement = wrapper3d;
            this.rootElement.appendChild(wrapper3d);
        }
        else {
            this.rootElement.appendChild(htmlElement);
        }
        return htmlElement;
    }

    async injectFontFace(fontStyle: IFontStyle): Promise<void> {
        try {
            const fontFace = await injectFontFace(fontStyle);
            if (!this._browserDocument.fonts.has(fontFace)) {
                this._browserDocument.fonts.add(fontFace);
            }
        }
        catch (err) {
            // Do nothing.
            handleError('Could not load font', {
                enabled: this.env_m.STUDIO_JS,
                contexts: {
                    font: fontStyle,
                    originalError: err
                }
            });
        }
    }

    private _getDataElementById(id: string): OneOfElementDataNodes | undefined {
        return this.storedViewElements.get(id)?.__data;
    }

    getScale_m(): number {
        return this._scale;
    }

    private _setScale(scale: number): void {
        if (scale === this._scale) {
            return;
        }

        if (scale === 0) {
            this.logger?.verbose('Trying to set scale to 0. Aborting.');
            return;
        }

        this.logger?.debug('setting scale: ' + scale);

        this._scale = scale;
        this._scaleViewTree(this.creativeDocument);
    }

    private _setScaleAndUpdate(scale: number): void {
        this._setScale(scale);

        // workaround because resize observer will trigger updates too early and we dont have a safer way to check everything was inited yet
        if (this.creativeDocument.elements.every(element => this.storedViewElements.has(element.id))) {
            this.updateCreativeValues();
            this.updateViewElementValues();
        }
    }

    private _scaleViewTree(size: any): void {
        this.viewTree.width = size.width * this._scale;
        this.viewTree.height = size.height * this._scale;
    }

    private _calculateScale(width: number, originalWidth: number): number {
        return width / originalWidth;
    }

    private _initResizeObserver(host: Element): void {
        if (this.ad?.responsive && typeof ResizeObserver !== 'undefined') {
            this.logger?.verbose('initializing ResizeObserver');
            this._resizeObserver = new ResizeObserver((observerEntries) => {
                this.logger?.debug('ResizeObserver: resize observed');
                const entry = observerEntries[0];
                const box = entry.borderBoxSize && entry.borderBoxSize[0];
                let width = box?.inlineSize; // Safari returns inlineSize undefined

                if (typeof width !== 'number') {
                    const boundingRect = entry.target.getBoundingClientRect();
                    width = boundingRect.width;
                }

                const newScale = this._calculateScale(width, this.creativeDocument.width);
                this._setScaleAndUpdate(newScale);
            });

            this._resizeObserver.observe(host);
        }
    }

    private _resetViewTree(): void {
        this.viewTree = { kind: NodeKind.Creative, elements: [], width: this.creativeDocument.width, height: this.creativeDocument.height };
    }

    hideElement_m(element: OneOfElementDataNodes): void {
        this.destroyElement_m(element, false);
    }

    showElement_m(element: OneOfElementDataNodes): void {
        visitOneNode(element, this);
    }
}
